/**
 * 文字工具（M2-18 / DESIGN.md §6.3）。
 *
 * 编辑用**浮动 textarea**：这是画布应用处理文本输入的标准做法——
 * IME（拼音等组合输入）由浏览器原生处理，组合过程完全不触碰历史；
 * 只有显式提交（Ctrl/⌘+Enter 或点击画布外）才产生命令。Esc 放弃。
 *
 * 点击 = 点文本（不折行）；拖出一个框 = 段落文本（按框宽折行）。
 * 双击已有文本图层 = 重新编辑。
 *
 * 富文本混合格式（M4-38，用户反馈"拖选一部分文字改字体/颜色"）：编辑器
 * 本身还是一个纯文本 `<textarea>`（IME/原生文本选区都是浏览器免费给的，
 * 不做 contenteditable 那一整套富文本 DOM），样式区间用
 * `interaction/tools/rich-text-runs.ts` 的纯函数在编辑会话里单独维护，
 * 只有真正提交时才转成 `TextRun[]`——编辑过程中即使选中了一段以不同
 * 颜色/字体高亮，`<textarea>` 本身仍然只显示纯文本，真正的混合渲染
 * 效果要提交后在画布上才看得到。这是刻意的简化：一个完整的所见即所得
 * 富文本编辑器需要 contenteditable + 自定义选区渲染，工作量和这次
 * "选中一段改样式"的实际需求不成比例。
 */
import type { DocumentPoint } from '../viewport';
import { layerId as makeLayerId, type LayerId } from '../../engine/tile/key';
import { createSetTextCommand, createTextLayer } from '../../app/commands/text-commands';
import { createAddLayerCommand } from '../../app/commands/layer-commands';
import { rasterizeText, type TextSpecRun } from '../../app/text/raster';
import { layoutTextSpans, type LayoutOptions, type StyledSpan } from '../../engine/text/layout';
import type { FontLibrary } from '../../app/text/fonts';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';
import type { TextLayer, TextRun, VectorFill, VectorStroke } from '../../document/types';
import { findLayer } from '../../document/draft';
import {
  applyStyleToRange,
  diffEdit,
  reflowRunsForEdit,
  richRunsFromTextRuns,
  runsFromText,
  sliceRuns,
  type RichRun,
  type RunStyle,
} from './rich-text-runs';

/**
 * `#hitTextLayer` 命中框的外扩容差（文档坐标单位）。真实鼠标点击/拖拽
 * 起点很容易落在文字墨迹紧邻外侧一两像素，紧贴 ink 边界判定会把这种
 * 情况误判成"点在空白处新建文本"，而不是命中已有文字。
 */
const TEXT_HIT_MARGIN = 6;

export interface TextToolDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly fonts: FontLibrary;
  readonly requestRender: () => void;
  /** 文档坐标 → 页面坐标（textarea 定位用）。 */
  readonly toScreen: (point: DocumentPoint) => { x: number; y: number };
  /** 当前视口缩放——编辑器的字号要跟着缩放走，否则在非 100% 缩放下会跟画布上最终栅格化的大小对不上。 */
  readonly viewportScale: () => number;
  /** 属性栏当前的填充取色（纯色/渐变），提交那一刻才读一次。 */
  readonly fill: () => VectorFill;
  /** 属性栏当前的描边取色+宽度；宽度为 0 视为不描边。 */
  readonly stroke: () => VectorStroke | undefined;
  /**
   * 隐藏画布上某个图层的已落盘像素——跟 `move-tool.ts`/`transform-tool.ts`
   * 拖拽预览期间隐藏原图层同一个机制（`bridge.setHiddenLayer`）。重新
   * 编辑既有文本时用：编辑器只是一个浮动 `<textarea>`，用浏览器自己的
   * 字体渡染预览，跟 `raster.ts` 用 harfbuzz+canvas 画出来的真实字形在
   * 位置/字号上不会逐像素一致——编辑期间如果画布上已落盘的旧像素还留着，
   * 看起来就像"点出来一份跟原文字挤在一起、又不完全重叠的复制品"
   * （用户反馈的真实现象）。隐藏旧像素，编辑期间就只看得到浮动编辑器
   * 这一份，提交时再重新栅格化写回、取消隐藏。
   */
  readonly setHiddenLayer: (id: string | null) => void;
}

interface Session {
  readonly layerId: LayerId | null; // null = 新建
  readonly origin: DocumentPoint;
  readonly boxWidth: number;
  readonly textarea: HTMLTextAreaElement;
  /** 点文本（非段落框）量宽用的隐藏镜像元素；段落文本固定宽度，不需要。 */
  readonly measurer: HTMLSpanElement | null;
  /** 样式区间——只有 `customized` 时才是"真相"，否则提交时无视它，
   * 整段文字统一用当时的属性栏默认值（保持没碰过富文本功能的用户
   * 体验跟这个功能上线前完全一样）。 */
  richRuns: RichRun[];
  /** 上一次 `input` 事件时的 textarea 内容，`diffEdit` 用来定位这次编辑改了哪一段。 */
  previousValue: string;
  /** 用户是不是已经对着某段选区应用过样式——重新编辑既有多样式文本时
   * 一开始就是 true（已有的样式必须保留，不能被"整段用当前默认值"冲掉）。 */
  customized: boolean;
}

export class TextToolController {
  readonly #deps: TextToolDeps;
  #session: Session | null = null;
  #dragStart: DocumentPoint | null = null;
  #seq = 0;

  constructor(deps: TextToolDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#session !== null;
  }

  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: DocumentPoint): boolean {
    if (this.#deps.tools.active.id !== 'text') return false;
    const document = this.#deps.store.document;
    if (document === null) return true;

    if (this.#session !== null) {
      // 编辑中点画布 = 提交（textarea 失焦也会提交，这里兜底）
      if (phase === 'down') this.commit();
      return true;
    }

    if (phase === 'double') {
      const hit = this.#hitTextLayer(point);
      if (hit !== null) {
        this.#openEditor(hit.id as LayerId, { x: hit.box.x, y: hit.box.y }, hit.box.width, hit, point);
        return true;
      }
    }
    if (phase === 'down') {
      // 命中已有文本（松手时才真正打开编辑，见下面 'up' 分支的长注释）
      // 也可能是新建点文本/拖段落框——两者都要记住按下的位置，等松手
      // 再决定具体是哪一种，这里先不分岔。
      this.#dragStart = point;
      return true;
    }
    if (phase === 'up' && this.#dragStart !== null) {
      const start = this.#dragStart;
      this.#dragStart = null;

      // 点在已有文本图层上 = 直接进入编辑（跟真实 Photoshop 一致：Type
      // 工具单击已有文字图层就能编辑，不需要等双击），光标落在按下处
      // 对应的字符上，松手处如果跟按下处不是同一个字符就是拖选一段
      // 文字——不是永远跳回开头/结尾，也不是永远只有一个光标——用户
      // 反馈"回车提交后，图层还选中着，却点不动文字中间、也选不了文字"。
      //
      // **必须在 'up'（松手）而不是 'down'（按下）阶段打开编辑器**，
      // 这是排查这个具体 bug 时踩到的一个真实的浏览器坑：如果在 'down'
      // 阶段就同步创建并 focus() 这个浮动 <textarea>，紧跟着到来的原生
      // mousedown 事件的默认行为（点在一个本身不可获得焦点的画布 div
      // 上）会把刚抢到的焦点立刻夺回去、触发 blur→自动提交，编辑器
      // 前脚开后脚就被自己的提交逻辑关掉——表现为看起来"什么都没有
      // 发生"（内部状态显示会话确实开过，但 DOM 里的 <textarea> 转眼就
      // 没了）。鼠标松开（mouseup）没有这条"默认抢焦点"的行为，所以
      // 挪到 'up' 阶段处理就完全绕开了这个坑；新建点文本/段落框本来就
      // 是在 'up' 阶段处理的，这次只是让"命中已有文本"也走同一条安全
      // 的时机，不是巧合各选各的。
      const hit = this.#hitTextLayer(start);
      if (hit !== null) {
        const opened = this.#openEditor(hit.id as LayerId, { x: hit.box.x, y: hit.box.y }, hit.box.width, hit, start);
        if (opened !== null) {
          const endIndex = this.#characterIndexAtPoint(hit, point);
          if (endIndex !== opened.cursorIndex) {
            opened.textarea.setSelectionRange(Math.min(opened.cursorIndex, endIndex), Math.max(opened.cursorIndex, endIndex));
          }
        }
        return true;
      }

      const width = Math.abs(point.x - start.x);
      const origin = { x: Math.min(start.x, point.x), y: Math.min(start.y, point.y) };
      // 拖出 >24px 视为段落框，否则点文本
      this.#openEditor(null, width > 24 ? origin : start, width > 24 ? width : 0, null);
      return true;
    }
    return true;
  }

  /** 属性栏当前值组成的样式——没碰过富文本混合格式功能时，整段文字用这一份。 */
  #currentDefaultStyle(): RunStyle {
    const deps = this.#deps;
    const tools = deps.tools;
    const fontFamily =
      (tools.getOption('text', 'fontFamily') as string | undefined) ?? deps.fonts.defaultFamily;
    const fontSize = tools.getOption('text', 'fontSize') as number;
    const fill = deps.fill();
    const stroke = deps.stroke();
    return { fontFamily, fontSize, fill, ...(stroke === undefined ? {} : { stroke }) };
  }

  /**
   * 把属性栏的字体/字号/颜色改动应用到"当前该改的地方"——调用方
   * （`main.tsx` 的取色器/字体下拉/系统字体导入回调，一共四个入口）
   * 不需要关心此刻是不是在编辑：编辑会话开着就只改选中的那段文字
   * （富文本混合格式，M4-38；没有选区——只有光标——时故意不改已经
   * 打好的文字，回退到旧行为，见测试），会话没开着（比如刚提交完、
   * 切到别的工具又切回来）就整层应用到当前选中的文字图层（用户反馈
   * "改变颜色仍然没有效果"排查出来的分支，M4-40d）。这两条路径以前
   * 分别是两个公开方法，路由判断散落在 `main.tsx` 四个调用点里——
   * 这次收进来，调用方少一处就少一处忘记接哪一条路的风险。
   */
  applyStyle(patch: Partial<RunStyle>): boolean {
    return this.#session !== null
      ? this.#applyStyleToSelection(patch)
      : this.#applyStyleToActiveLayer(patch);
  }

  /**
   * 只应用到当前编辑器里选中的那段文字——没有编辑会话（不会发生，
   * 调用方 `applyStyle` 已经判过）或选区是空的（只有光标，没有真正
   * 选中任何字符）时返回 `false`，故意不改已经打好的文字。
   *
   * ⚠️`selectionStart`/`selectionEnd` 是 `<textarea>` 的 UTF-16 code unit
   * 下标，`RichRun` 按 Unicode 码点（`[...text]`）计数——选区之前如果
   * 出现代理对字符（比如 emoji），两种下标会不一致，样式区间可能因此
   * 错位几个字符。已披露的简化：正常的中英文（含 CJK 基本平面）不受
   * 影响，只有真正打进 emoji 之类增补平面字符时才可能出现。
   */
  #applyStyleToSelection(patch: Partial<RunStyle>): boolean {
    const session = this.#session;
    if (session === null) return false;
    const { textarea } = session;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    if (start === end) return false;
    if (!session.customized) {
      session.richRuns = runsFromText([...textarea.value].length, this.#currentDefaultStyle());
      session.customized = true;
    }
    session.richRuns = applyStyleToRange(session.richRuns, start, end, patch);
    return true;
  }

  /**
   * 编辑会话没开着，把新样式整体应用到当前选中的、已提交的文字图层的
   * **全部**字符上——没有打开编辑会话就没有"选区"这个概念，只能整段
   * 应用；跟 `commit()`、`main.tsx` 的 `text.toggleWritingMode` 同一套
   * 「改 `TextRun[]` + dispatch `createSetTextCommand` + 重新栅格化」
   * 组合，不是另起一套。
   */
  #applyStyleToActiveLayer(patch: Partial<RunStyle>): boolean {
    const deps = this.#deps;
    const doc = deps.store.document;
    const id = deps.store.activeLayerId;
    if (doc === null || id === null) return false;
    const found = findLayer(doc.root, id);
    if (found === null || found.layer.kind !== 'text') return false;
    const layer = found.layer;

    const specRuns: TextSpecRun[] = [];
    const textRuns: TextRun[] = [];
    for (const run of layer.runs) {
      const fontFamily = patch.fontFamily ?? run.fontFamily;
      const fontSize = patch.fontSize ?? run.fontSize;
      const fill = patch.fill ?? run.fill;
      const stroke = 'stroke' in patch ? patch.stroke : run.stroke;
      const shaper = deps.fonts.shaperFor(fontFamily);
      if (shaper === null) {
        deps.store.setHint('字体尚未加载完成');
        return false;
      }
      specRuns.push({ text: run.text, shaper, fontSize, fill, ...(stroke === undefined ? {} : { stroke }) });
      textRuns.push({
        text: run.text,
        fontFamily,
        fontSize,
        fill,
        letterSpacing: run.letterSpacing,
        lineHeight: run.lineHeight,
        ...(stroke === undefined ? {} : { stroke }),
      });
    }
    if (textRuns.length === 0) return false;

    deps.store.dispatch(createSetTextCommand(id, textRuns, layer.box));
    const transaction = deps.store.beginPixelTransaction('文本');
    const result = rasterizeText(
      deps.store.tiles,
      id,
      {
        runs: specRuns,
        lineHeight: layer.runs[0]?.lineHeight ?? 1.5,
        boxWidth: layer.box.width,
        writingMode: layer.writingMode,
        ...(layer.warp === undefined ? {} : { warp: layer.warp }),
      },
      { x: layer.box.x, y: layer.box.y },
      transaction,
    );
    deps.store.commitPixels('文本', transaction.end());
    deps.store.invalidatePixels([...result.dirty]);
    deps.requestRender();
    return true;
  }

  /**
   * 编辑中时把焦点还给 <textarea>——字体下拉、取色器这些改样式的入口都在
   * 编辑器之外，点它们本来就会让 <textarea> 失焦，取色器关掉之后浏览器
   * 不会自己把焦点还回来。不还的话，接下来的 Ctrl/⌘+Enter 提交、继续
   * 输入/选字这些键盘操作全都送不到编辑会话上——不管这次改动有没有
   * 真的应用到一段选区（`applyStyle` 的返回值），只要编辑
   * 会话还在，用完这些控件都该把焦点还回去，调用方（`main.tsx`）在每个
   * 字体/颜色改动的入口都要调这个，不能只在选区命中时才调。
   */
  refocus(): void {
    this.#session?.textarea.focus();
  }

  /**
   * 文档空间叠加：编辑中的文字周围画一个跟变换工具同色系的选中框
   * （PhotoPea/Photoshop 进入文字编辑态都有这样一条边框反馈，标出"当前
   * 正在编辑的是这一块"；之前完全没有，只能靠浮动 `<textarea>` 本身透明
   * 背景的可见文字判断范围，行末/行首在哪一点说不清楚）。跟
   * `transform-tool.ts#drawOverlay` 同一个惯例：调用方（`main.tsx`）在
   * 每帧的 `setDocOverlay` 回调里传当前视口缩放，线宽换算成常数屏幕像素，
   * 不随缩放变粗/变细。
   */
  drawOverlay(ctx: CanvasRenderingContext2D, viewportScale: number): void {
    const session = this.#session;
    if (session === null) return;
    // textarea 自身的屏幕像素尺寸（宽度跟着内容自适应、高度跟着行数长，
    // 都已经在 autoSize 里维护好了）换算回文档坐标——跟 `screen = deps.toScreen(origin)`
    // 用的是同一套"fixed 定位 = 页面像素坐标"约定，不需要另外维护一份
    // 冗余的文档空间宽高。
    const rect = session.textarea.getBoundingClientRect();
    const width = rect.width / viewportScale;
    const height = rect.height / viewportScale;
    ctx.save();
    ctx.strokeStyle = 'rgba(64,128,255,0.9)';
    ctx.lineWidth = 1 / viewportScale;
    ctx.strokeRect(session.origin.x, session.origin.y, width, height);
    ctx.restore();
  }

  /** 提交当前编辑（供 Enter 键路由与失焦调用）。 */
  commit(): void {
    const session = this.#session;
    if (session === null) return;
    const text = session.textarea.value;
    this.#closeEditor();
    if (text.trim() === '') return;

    const deps = this.#deps;
    const richRuns = session.customized
      ? session.richRuns
      : runsFromText([...text].length, this.#currentDefaultStyle());

    // 逐条 run 解析字体——某一款还没加载完成就整段拒绝提交，跟原来单一
    // 样式时"字体尚未加载完成"的兜底一致，只是现在要检查全部用到的字体
    const specRuns: TextSpecRun[] = [];
    const textRuns: TextRun[] = [];
    for (const run of sliceRuns(richRuns, text)) {
      if (run.text.length === 0) continue; // reflow 保证区间非空，这里只是防御一下
      const shaper = deps.fonts.shaperFor(run.fontFamily);
      if (shaper === null) {
        deps.store.setHint('字体尚未加载完成');
        return;
      }
      specRuns.push({
        text: run.text,
        shaper,
        fontSize: run.fontSize,
        fill: run.fill,
        ...(run.stroke === undefined ? {} : { stroke: run.stroke }),
      });
      textRuns.push({
        text: run.text,
        fontFamily: run.fontFamily,
        fontSize: run.fontSize,
        fill: run.fill,
        letterSpacing: 0,
        lineHeight: 1.5,
        ...(run.stroke === undefined ? {} : { stroke: run.stroke }),
      });
    }
    if (textRuns.length === 0) return; // 全部是空 run（理论上不该发生），安全兜底不提交

    const box = {
      x: session.origin.x,
      y: session.origin.y,
      width: session.boxWidth,
      height: 0,
    };

    let target = session.layerId;
    if (target === null) {
      target = makeLayerId(`text-${++this.#seq}-${deps.store.document!.root.children.length}`);
      const layer = createTextLayer(target, text.slice(0, 12) || '文本', textRuns, box);
      deps.store.dispatch(
        createAddLayerCommand({
          layer,
          parentId: null,
          index: deps.store.document!.root.children.length,
        }),
      );
      deps.store.setActiveLayer(target); // 新建即活动（与画笔/形状/调整图层同一惯例）
    } else {
      deps.store.dispatch(createSetTextCommand(target, textRuns, box));
    }

    const transaction = deps.store.beginPixelTransaction('文本');
    const result = rasterizeText(
      deps.store.tiles,
      target,
      { runs: specRuns, lineHeight: 1.5, boxWidth: session.boxWidth },
      session.origin,
      transaction,
    );
    deps.store.commitPixels('文本', transaction.end());
    deps.store.invalidatePixels([...result.dirty]);
    deps.requestRender();
  }

  cancel(): void {
    this.#closeEditor();
  }

  #openEditor(
    layer: LayerId | null,
    origin: DocumentPoint,
    boxWidth: number,
    existing: TextLayer | null,
    /** 触发这次打开的真实点击位置（文档空间）——重新编辑既有文本时，
     * 用它算出光标该落在哪个字符位置（`#characterIndexAtPoint`）；新建
     * 文本没有这个概念（光标永远在末尾，textarea 原生行为）。 */
    clickPoint?: DocumentPoint,
  ): { readonly textarea: HTMLTextAreaElement; readonly cursorIndex: number } | null {
    const deps = this.#deps;
    const tools = deps.tools;
    const family =
      (tools.getOption('text', 'fontFamily') as string | undefined) ?? deps.fonts.defaultFamily;
    const fontSize = tools.getOption('text', 'fontSize') as number;
    const scale = deps.viewportScale();
    const screen = deps.toScreen(origin);
    const isParagraph = boxWidth > 0;

    // 重新编辑既有文本：把画布上已落盘的旧像素先隐藏（跟 move/transform
    // 工具拖拽预览同一个机制），不然浮动 textarea 的浏览器字体渲染跟
    // 已落盘的 harfbuzz+canvas 真实字形在位置/字号上不会逐像素一致，
    // 编辑期间两份叠在一起看起来就像"多出一份跟原文字挤在一起、又不
    // 完全重叠的复制品"——这正是用户反馈的现象，提交/取消时再取消隐藏
    // （`#closeEditor` 统一处理，不用在每个提交/取消分支各写一遍）。
    if (existing !== null) {
      deps.setHiddenLayer(existing.id);
      // `setHiddenLayer` 本身只标记渲染缓存整体失效，不会自己触发重绘
      // （跟 move-tool.ts 同一个既有约定）——不补这一下，画面要等到下一次
      // 别的原因触发重绘才会刷新，编辑刚打开那一下旧像素还在画面上
      deps.requestRender();
    }

    const textarea = document.createElement('textarea');
    textarea.value = existing?.runs.map((r) => r.text).join('') ?? '';
    textarea.setAttribute('data-testid', 'text-editor');
    textarea.spellcheck = false;
    // 不给 rows 的话 <textarea> 默认按 2 行算内在高度——`height:auto` 只会
    // 解析回那个 2 行基准，scrollHeight 也跟着被这个下限锁住，一行文字的
    // 编辑器看起来永远是两行高。显式钉成 1 行，autoSize 才量得出真实内容高度。
    textarea.rows = 1;
    // 跟 Photoshop 一致：编辑器只是画布上飘着的文字，不是一个带边框/
    // 白底的表单控件——透明背景、无边框无内边距，视觉上就是"字长在画布上"。
    textarea.style.position = 'fixed';
    textarea.style.left = `${screen.x}px`;
    textarea.style.top = `${screen.y}px`;
    textarea.style.zIndex = 'var(--z-popover)';
    textarea.style.fontFamily = family;
    textarea.style.fontSize = `${fontSize * scale}px`;
    textarea.style.lineHeight = '1.5';
    // 渐变没法在一个纯文本 <textarea> 里预览，编辑态退到纯色（或黑色兜底）；
    // 真正的渐变/描边效果在提交时栅格化才画出来
    const previewFill = deps.fill();
    textarea.style.color = previewFill.type === 'solid' ? previewFill.color : '#000000';
    textarea.style.background = 'transparent';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.padding = '0';
    textarea.style.margin = '0';
    textarea.style.resize = 'none';
    textarea.style.overflow = 'hidden';
    if (isParagraph) {
      // 段落框：宽度是用户拖出来的，固定不变，只有高度跟着行数长
      textarea.style.width = `${boxWidth * scale}px`;
      textarea.style.whiteSpace = 'pre-wrap';
      textarea.style.wordBreak = 'break-word';
    } else {
      // 点文本：不折行，宽度跟着最长一行的实际像素宽度长——见下面的镜像量宽
      textarea.style.whiteSpace = 'pre';
    }
    document.body.append(textarea);

    // 点文本量宽：textarea 自身没有"内容多宽"这个量，用一个同字体同字号、
    // 不可见的镜像 <span> 撑开来读 offsetWidth，段落框宽度固定用不上这个
    let measurer: HTMLSpanElement | null = null;
    if (!isParagraph) {
      measurer = document.createElement('span');
      measurer.style.position = 'fixed';
      measurer.style.top = '-9999px';
      measurer.style.left = '-9999px';
      measurer.style.visibility = 'hidden';
      measurer.style.whiteSpace = 'pre';
      measurer.style.fontFamily = family;
      measurer.style.fontSize = `${fontSize * scale}px`;
      document.body.append(measurer);
    }

    const autoSize = (): void => {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
      if (measurer === null) return;
      let maxWidth = 0;
      for (const line of textarea.value.split('\n')) {
        measurer.textContent = line.length > 0 ? line : ' ';
        maxWidth = Math.max(maxWidth, measurer.offsetWidth);
      }
      textarea.style.width = `${maxWidth + 2}px`;
    };
    // 每次 textarea 内容变化都在这里重新排布样式区间（富文本混合格式，
    // M4-38）——不只是原生 `input` 事件，Alt+Enter 手动插换行符那条路径
    // 也要走一遍，两处共用同一份逻辑，不能各自维护一份
    const handleTextChanged = (): void => {
      const session = this.#session;
      if (session !== null) {
        if (session.customized) {
          const edit = diffEdit(session.previousValue, textarea.value);
          session.richRuns = reflowRunsForEdit(session.richRuns, edit, this.#currentDefaultStyle());
        }
        session.previousValue = textarea.value;
      }
      autoSize();
      // 编辑框的选中态边框（`drawOverlay`）画的是 textarea 当前的屏幕
      // 尺寸——内容变化后宽高跟着 autoSize 变了，不主动请求重绘的话，
      // 边框要等到下一次别的原因触发重绘才会跟上，看起来像是"框跟文字
      // 脱节了一下"。
      deps.requestRender();
    };
    autoSize();
    textarea.focus();

    textarea.addEventListener('input', handleTextChanged);
    textarea.addEventListener('keydown', (event) => {
      // 组合输入（IME）期间的 Enter 属于输入法，不拦
      if (event.isComposing) return;
      if (event.key === 'Enter' && event.altKey) {
        // Option/Alt+Enter 手动换行——裸 Enter 占用给了提交，浏览器不会
        // 自己在这种按键组合下往 textarea 里插换行符，得手动插入
        event.preventDefault();
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        textarea.value = `${textarea.value.slice(0, start)}\n${textarea.value.slice(end)}`;
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = start + 1;
        handleTextChanged();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this.commit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.cancel();
      }
      event.stopPropagation(); // 别让 B/V 等单字母键切走工具
    });
    textarea.addEventListener('blur', (event) => {
      // 失焦提交（点击画布外/切窗口）。Esc 已经先 close 了就不会到这。
      // 例外：焦点挪去了工具属性栏/取色器（`[data-keep-text-session]`，
      // M4-38——选中一段文字后要点"字体"下拉或"颜色"色块才能改样式，
      // 这些控件本来就在 <textarea> 之外，点击必然触发失焦，但这不是
      // 用户想放弃编辑，提交反而会把刚选中的文字连同编辑会话一起冲掉）
      const related = event.relatedTarget;
      if (related instanceof Element && related.closest('[data-keep-text-session]') !== null) return;
      if (this.#session?.textarea === textarea) this.commit();
    });

    // 重新编辑既有的（可能早就是多样式的）文本，用它真实的 run 区间；
    // 新建文本还没有任何内容，`customized` 留 false——不碰选区改样式这个
    // 新功能的话，整段提交时照旧统一用当时的属性栏默认值，行为不变
    this.#session = {
      layerId: layer,
      origin,
      boxWidth,
      textarea,
      measurer,
      richRuns: existing === null ? [] : richRunsFromTextRuns(existing.runs),
      previousValue: textarea.value,
      customized: existing !== null,
    };

    // 点在既有文本上重新编辑时，光标定位到点击处对应的字符，不是永远
    // 跳回开头/结尾（用户反馈"回车提交后无法再点文字中间/选文字"）
    if (existing !== null && clickPoint !== undefined) {
      const index = this.#characterIndexAtPoint(existing, clickPoint);
      textarea.setSelectionRange(index, index);
      return { textarea, cursorIndex: index };
    }
    return null;
  }

  /**
   * 横排（非竖排、非变形）文字真正排版一遍——`#hitTextLayer`（算命中范围）
   * 和 `#characterIndexAtPoint`（点哪定位到哪个字符）共用同一份结果，
   * 不重复实现排版逻辑。字体没加载完成时返回 `null`（调用方各自决定
   * 怎么退回一个粗略估算的兜底）。
   */
  #layoutFor(layer: TextLayer): { readonly layout: ReturnType<typeof layoutTextSpans>; readonly fontSize: number } | null {
    if (layer.writingMode === 'vertical' || layer.warp !== undefined) return null;
    const spans: StyledSpan[] = [];
    for (const run of layer.runs) {
      const shaper = this.#deps.fonts.shaperFor(run.fontFamily);
      if (shaper === null) return null; // 字体没加载完成，没法排版
      spans.push({ text: run.text, shaper, fontSize: run.fontSize });
    }
    const fontSize = Math.max(...layer.runs.map((r) => r.fontSize));
    const options: LayoutOptions = {
      fontSize,
      lineHeight: layer.runs[0]?.lineHeight ?? 1.5,
      ...(layer.box.width > 0 ? { maxWidth: layer.box.width } : {}),
    };
    return { layout: layoutTextSpans(spans, options), fontSize };
  }

  /**
   * 把文档空间的一个点映射成 `layer` 文字内容里最近的字符下标（点哪个
   * 字符附近，光标/选区就落在哪）——真实用真正的排版引擎算字形位置，
   * 不是拍脑袋估的。已披露的简化：竖排文字、变形（warp）文字，以及
   * 字体还没加载完成时，一律退回下标 0（光标落在最前面），不逐字符
   * 定位——这几种情形的排版跟点击点的映射关系更复杂，跟这次"点哪就
   * 定位到哪"的核心需求（横排纯文本、最常见场景）不成比例。
   */
  #characterIndexAtPoint(layer: TextLayer, point: DocumentPoint): number {
    const fullText = layer.runs.map((r) => r.text).join('');
    const totalChars = [...fullText].length;
    if (totalChars === 0) return 0;

    const resolved = this.#layoutFor(layer);
    if (resolved === null) return 0;
    const { layout, fontSize } = resolved;
    const lineHeight = layer.runs[0]?.lineHeight ?? 1.5;
    if (layout.lines.length === 0) return 0;

    const localX = point.x - layer.box.x;
    const localY = point.y - layer.box.y;
    const lineHeightPx = fontSize * lineHeight;
    const lineIndex = Math.max(0, Math.min(layout.lines.length - 1, Math.floor(localY / lineHeightPx)));
    const line = layout.lines[lineIndex]!;
    if (line.start >= line.end) return line.start; // 空行（比如连续换行）

    // 每个字符簇的左边缘 x——取同一簇里最靠左的那个 glyph（多数字符
    // 一簇一个 glyph，连字/组合字符例外，取左边缘依然是合理的落点）
    const leftEdge = new Map<number, number>();
    for (const glyph of layout.glyphs) {
      const existing = leftEdge.get(glyph.cluster);
      if (existing === undefined || glyph.x < existing) leftEdge.set(glyph.cluster, glyph.x);
    }
    let best = line.start;
    let bestDistance = Infinity;
    for (let cluster = line.start; cluster < line.end; cluster++) {
      const x = leftEdge.get(cluster);
      if (x === undefined) continue;
      const distance = Math.abs(x - localX);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = cluster;
      }
    }
    // 落点比这一行最后一个字符还靠右——光标该在行尾之后，不是卡在
    // 最后一个字符前面
    if (localX > (leftEdge.get(line.end - 1) ?? 0) && line.end <= totalChars) return line.end;
    return best;
  }

  #closeEditor(): void {
    const session = this.#session;
    this.#session = null;
    session?.textarea.remove();
    session?.measurer?.remove();
    // 无条件取消隐藏——新建文本从来没隐藏过任何图层，这里跟着调一次
    // 也是无害的空操作（同 move-tool.ts 提交/取消时的既有写法）。
    // `commit()` 之后自己还会因为重新栅格化再调一次 `requestRender()`，
    // 这里为了 `cancel()`（Esc，不会再有后续任何重绘触发点）也能正确
    // 把取消隐藏的画面刷出来，两边都补一次，重复调用无害
    this.#deps.setHiddenLayer(null);
    this.#deps.requestRender();
  }

  #hitTextLayer(point: DocumentPoint): TextLayer | null {
    const document = this.#deps.store.document;
    if (document === null) return null;
    for (let i = document.root.children.length - 1; i >= 0; i--) {
      const layer = document.root.children[i]!;
      if (layer.kind !== 'text') continue;
      const box = layer.box;
      const fontSize = layer.runs[0]?.fontSize ?? 24;
      const lineHeight = layer.runs[0]?.lineHeight ?? 1.5;
      // 优先用真正排版算出来的尺寸——字体没加载完成时才退回粗略估算。
      const resolved = this.#layoutFor(layer);
      let width: number;
      let height: number;
      if (resolved !== null) {
        width = box.width > 0 ? box.width : Math.max(fontSize * 2, resolved.layout.width);
        height = Math.max(box.height, resolved.layout.height, fontSize * lineHeight);
      } else {
        // 点文本没有段落框那种显式宽度，按字符数 × 字号粗略估个宽容命中区
        // （字体的真实字符平均宽度这里不精确知道，用 1 个字号当每字符宽度
        // 是常见的粗略估算，够点一下命中、又不会宽到离谱）。之前这里固定
        // 给 400px——两个点文本挨着放在同一行时，只要横向距离不超过 400，
        // 点第二个文本反而会先命中第一个文本，把"点在空白处新建文本"误判
        // 成"点在已有文本上要重新编辑"（M4-38 排查文字工具单击命中范围时
        // 发现的真实 bug，e2e 复现过）。高度同理不能只给一个字号——那只够
        // 单行文字的一小部分，行高（含行间距）才是真实一行占的高度，
        // 点在一行文字偏下的位置会点不中（用户反馈"回车提交后点不动
        // 文字"排查到的另一个真实成因）。
        const totalChars = layer.runs.reduce((n, r) => n + [...r.text].length, 0);
        width = box.width > 0 ? box.width : Math.max(fontSize * 2, fontSize * totalChars);
        height = Math.max(box.height, fontSize * lineHeight);
      }
      // 命中框外扩一点容差——真实鼠标拖拽起点很容易落在文字实际墨迹的
      // 紧邻外侧一两像素（比如想"从第一个字符最左边开始拖选到最后一个
      // 字符"，手感上很自然会先落在稍微靠左一点的位置），紧贴 ink 边界
      // 判定会把这种拖拽误判成"点在空白处"，从而新开一个空文本框、
      // 而不是命中已有文字（用户反馈"拖选/全选改颜色，结果冒出一段
      // 没变色的原文字，好像根本没选中它"，根因就在这里）。
      const margin = TEXT_HIT_MARGIN;
      if (
        point.x >= box.x - margin &&
        point.x <= box.x + width + margin &&
        point.y >= box.y - margin &&
        point.y <= box.y + height + margin
      ) {
        return layer;
      }
    }
    return null;
  }
}
